import React, { useState } from "react";
import { Box, Grid, Typography, Chip, Stack, alpha } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  DirectionsCar,
  TwoWheeler,
  TrendingUp,
  TrendingDown,
  Login as EntryIcon,
  Logout as ExitIcon,
  AccessTime,
  ShowChart,
  Assessment,
  BarChart as BarChartIcon,
  Speed,
  Insights,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { StatCard } from "../../components/common";
import HeroSection from "../../components/HeroSection";
import PageToolbar from "../../components/Toolbar";
import ScrollToTopFab from "../../components/ScrollToTopFab";
import MetricCard from "../../components/MetricCard";
import ViewTabs from "../../components/ViewTabs";
import ChartCard from "../../components/ChartCard";
import {
  transformTrafficData,
  calculateTrafficStats,
  ViewType,
} from "../../utils/dataTransformers";
import {
  useGetDailyAnalyticsQuery,
  useGetMonthlyAnalyticsQuery,
  useGetYearlyAnalyticsQuery,
} from "../../features/analytics/analyticsApiSlice";
import { TrafficMetrics } from "../../types";
import { useAppSelector } from "../../store/store";
import { useErrorToast } from "../../hooks";
import { gradients } from "../../utils/theme";
import { useAnalyticsDateParams } from "../../utils/dateFilterUtils";

type ChartType = "line" | "area" | "bar";

const Analytics: React.FC = () => {
  const [currentView, setCurrentView] = useState<ViewType>("daily");
  const [trendsChartType, setTrendsChartType] = useState<ChartType>("line");
  const [distributionChartType, setDistributionChartType] =
    useState<ChartType>("bar");

  // Use scroll-to-top hook for button visibility and scroll functionality

  const dateParams = useAnalyticsDateParams();
  // venueId is extracted from JWT token by backend automatically

  // Check for cached data from DashboardContent
  const cachedDaily = useAppSelector(
    (state: any) => state.analytics.dailyCache,
  );
  const cachedMonthly = useAppSelector(
    (state: any) => state.analytics.monthlyCache,
  );
  const cachedYearly = useAppSelector(
    (state: any) => state.analytics.yearlyCache,
  );

  const isDailyCacheValid =
    cachedDaily.data &&
    cachedDaily.startDate === dateParams.startDate &&
    cachedDaily.endDate === dateParams.endDate &&
    cachedDaily.timestamp &&
    Date.now() - cachedDaily.timestamp < 60000; // 1 minute cache

  const isMonthlyCacheValid =
    cachedMonthly.data &&
    cachedMonthly.startDate === dateParams.startDate &&
    cachedMonthly.endDate === dateParams.endDate &&
    cachedMonthly.timestamp &&
    Date.now() - cachedMonthly.timestamp < 60000;

  const isYearlyCacheValid =
    cachedYearly.data &&
    cachedYearly.startDate === dateParams.startDate &&
    cachedYearly.endDate === dateParams.endDate &&
    cachedYearly.timestamp &&
    Date.now() - cachedYearly.timestamp < 60000;

  // RTK Query hooks - skip if we have valid cache or if tab is not active
  const {
    data: dailyData,
    isLoading: isDailyLoading,
    isFetching: isDailyFetching,
    isError: isDailyError,
  } = useGetDailyAnalyticsQuery(
    { ...dateParams },
    { skip: currentView !== "daily" || isDailyCacheValid },
  );

  const {
    data: monthlyData,
    isLoading: isMonthlyLoading,
    isFetching: isMonthlyFetching,
    isError: isMonthlyError,
  } = useGetMonthlyAnalyticsQuery(
    { ...dateParams },
    { skip: currentView !== "monthly" || isMonthlyCacheValid },
  );

  const {
    data: yearlyData,
    isLoading: isYearlyLoading,
    isFetching: isYearlyFetching,
    isError: isYearlyError,
  } = useGetYearlyAnalyticsQuery(
    { ...dateParams },
    { skip: currentView !== "yearly" || isYearlyCacheValid },
  );

  // Get current data based on selected view (use cache if available)
  const getCurrentData = () => {
    switch (currentView) {
      case "daily":
        return {
          data: isDailyCacheValid ? { data: cachedDaily.data } : dailyData,
          isLoading: isDailyCacheValid ? false : isDailyLoading,
          isFetching: isDailyCacheValid ? false : isDailyFetching,
        };
      case "monthly":
        return {
          data: isMonthlyCacheValid
            ? { data: cachedMonthly.data }
            : monthlyData,
          isLoading: isMonthlyCacheValid ? false : isMonthlyLoading,
          isFetching: isMonthlyCacheValid ? false : isMonthlyFetching,
        };
      case "yearly":
        return {
          data: isYearlyCacheValid ? { data: cachedYearly.data } : yearlyData,
          isLoading: isYearlyCacheValid ? false : isYearlyLoading,
          isFetching: isYearlyCacheValid ? false : isYearlyFetching,
        };
    }
  };

  const { data, isLoading, isFetching } = getCurrentData();

  // Determine current error state based on selected view
  const currentIsError =
    currentView === "daily"
      ? isDailyError
      : currentView === "monthly"
        ? isMonthlyError
        : isYearlyError;

  // Extract trafficMetrics from first item
  const trafficMetrics: TrafficMetrics | undefined =
    data?.data?.[0]?.trafficMetrics;

  // Transform traffic data using utility function
  // transformTrafficData now returns sample data points with 0 values if no data
  const chartData = transformTrafficData(trafficMetrics, currentView);

  // Calculate statistics using utility function
  const stats = calculateTrafficStats(chartData);

  // Handlers
  const handleTabChange = (_: React.SyntheticEvent, newValue: ViewType) => {
    setCurrentView(newValue);
  };

  const handleViewChange = (view: "daily" | "monthly" | "yearly") => {
    setCurrentView(view);
  };

  // Check if we're in initial loading state
  const isInitialLoading = isLoading && !data;

  // Chart renderers
  const renderTrendsChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 20, right: 30, left: 20, bottom: 20 },
    };

    const commonElements = (
      <>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey={currentView === "monthly" ? "displayTime" : "time"}
          stroke="#666"
          fontSize={12}
        />
        <YAxis stroke="#666" fontSize={12} />
        <RechartsTooltip
          contentStyle={{
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            border: "1px solid #ccc",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
          labelFormatter={(value: any, payload: any) => {
            // Show full formatted date in tooltip for monthly view
            if (currentView === "monthly" && payload && payload[0]) {
              return payload[0].payload.time;
            }
            return value;
          }}
        />
        <Legend />
      </>
    );

    switch (trendsChartType) {
      case "area":
        return (
          <AreaChart {...commonProps}>
            {commonElements}
            <Area
              type="monotone"
              dataKey="entries"
              stroke="#10b981"
              fill="#10b981"
              fillOpacity={0.6}
              name="Entries"
            />
            <Area
              type="monotone"
              dataKey="exits"
              stroke="#ef4444"
              fill="#ef4444"
              fillOpacity={0.6}
              name="Exits"
            />
          </AreaChart>
        );
      case "bar":
        return (
          <BarChart {...commonProps}>
            {commonElements}
            <Bar dataKey="entries" fill="#10b981" name="Entries" />
            <Bar dataKey="exits" fill="#ef4444" name="Exits" />
          </BarChart>
        );
      default: // line
        return (
          <LineChart {...commonProps}>
            {commonElements}
            <Line
              type="monotone"
              dataKey="entries"
              stroke="#10b981"
              strokeWidth={3}
              dot={{ fill: "#10b981", r: 4 }}
              name="Entries"
            />
            <Line
              type="monotone"
              dataKey="exits"
              stroke="#ef4444"
              strokeWidth={3}
              dot={{ fill: "#ef4444", r: 4 }}
              name="Exits"
            />
            <Line
              type="monotone"
              dataKey="occupancy"
              stroke="#3b82f6"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ fill: "#2196f3", r: 3 }}
              name="Occupancy"
            />
          </LineChart>
        );
    }
  };

  const renderVehicleDistributionChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 20, right: 30, left: 20, bottom: 20 },
    };

    const commonElements = (
      <>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey={currentView === "monthly" ? "displayTime" : "time"}
          stroke="#666"
          fontSize={12}
        />
        <YAxis stroke="#666" fontSize={12} />
        <RechartsTooltip
          contentStyle={{
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            border: "1px solid #ccc",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
          labelFormatter={(value: any, payload: any) => {
            // Show full formatted date in tooltip for monthly view
            if (currentView === "monthly" && payload && payload[0]) {
              return payload[0].payload.time;
            }
            return value;
          }}
        />
        <Legend />
      </>
    );

    switch (distributionChartType) {
      case "area":
        return (
          <AreaChart {...commonProps}>
            {commonElements}
            <Area
              type="monotone"
              dataKey="cars"
              stackId="1"
              stroke="#1a237e"
              fill="#1a237e"
              fillOpacity={0.7}
              name="Cars"
            />
            <Area
              type="monotone"
              dataKey="bikes"
              stackId="1"
              stroke="#4facfe"
              fill="#4facfe"
              fillOpacity={0.7}
              name="Bikes"
            />
          </AreaChart>
        );
      case "line":
        return (
          <LineChart {...commonProps}>
            {commonElements}
            <Line
              type="monotone"
              dataKey="cars"
              stroke="#1a237e"
              strokeWidth={2}
              dot={{ fill: "#1a237e", r: 4 }}
              name="Cars"
            />
            <Line
              type="monotone"
              dataKey="bikes"
              stroke="#4facfe"
              strokeWidth={3}
              dot={{ fill: "#4facfe", r: 4 }}
              name="Bikes"
            />
          </LineChart>
        );
      default: // bar
        return (
          <BarChart {...commonProps}>
            {commonElements}
            <Bar
              dataKey="cars"
              stackId="vehicles"
              fill="#1a237e"
              name="Cars"
              radius={[0, 0, 4, 4]}
            />
            <Bar
              dataKey="bikes"
              stackId="vehicles"
              fill="#4facfe"
              name="Bikes"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        );
    }
  };

  const renderVehicleTypePieChart = () => {
    const pieData = [
      { name: "Cars", value: stats.totalCars, fill: "#1a237e" },
      { name: "Bikes", value: stats.totalBikes, fill: "#4facfe" },
    ];

    return (
      <PieChart width={400} height={300}>
        <Pie
          data={pieData}
          cx="50%"
          cy="50%"
          outerRadius={100}
          dataKey="value"
          label={({ name, percent }) =>
            `${name} ${((percent as number) * 100).toFixed(0)}%`
          }
        >
          {pieData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.fill} />
          ))}
        </Pie>
        <RechartsTooltip />
        <Legend />
      </PieChart>
    );
  };

  const getViewLabel = () => {
    switch (currentView) {
      case "daily":
        return "Hourly";
      case "monthly":
        return "Daily";
      case "yearly":
        return "Monthly";
    }
  };

  // Handle error toast notifications with automatic deduplication and mount delay
  useErrorToast({
    isError: currentIsError,
    isFetching,
    isLoading,
    errorMessage: `Failed to load ${currentView} analytics data. Please try again.`,
    errorKey: currentView,
    resetDependencies: [currentView],
  });

  return (
    <Box>
      <PageToolbar viewContext={currentView} onViewChange={handleViewChange} />

      {/* Enhanced Hero Section with Real-time Indicator */}
      <HeroSection
        title="Traffic Flow Analytics"
        subtitle="Interactive charts and real-time insights for traffic patterns and vehicle distribution trends"
        gradient="royalBlue"
      >
        {/* Hero Actions Bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            mb: 2,
          }}
        ></Box>

        {/* View Selection Tabs in Hero */}
        <ViewTabs
          value={currentView}
          onChange={handleTabChange}
          tabs={[
            {
              value: "daily",
              label: "Daily",
              icon: <ShowChart />,
            },
            {
              value: "monthly",
              label: "Monthly",
              icon: <BarChartIcon />,
            },
            {
              value: "yearly",
              label: "Yearly",
              icon: <Assessment />,
            },
          ]}
        />
      </HeroSection>

      <Box sx={{ py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 2, md: 1.5 } }}>
        {/* Content - Always show with 0 values when no data */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`analytics-${currentView}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <Box sx={{ position: "relative", minHeight: 600 }}>
              {/* AI Insights Banner (if data shows interesting patterns) */}
              {stats.totalEntries > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <Box
                    sx={{
                      mb: 4,
                      p: 3,
                      background: `linear-gradient(135deg, ${alpha("#1a237e", 0.1)} 0%, ${alpha("#3949ab", 0.15)} 100%)`,
                      border: "2px solid",
                      borderColor: alpha("#1a237e", 0.2),
                      borderRadius: 3,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2.5,
                      boxShadow: "0 8px 24px rgba(26, 35, 126, 0.12)",
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "3px",
                        background:
                          "linear-gradient(90deg, #1a237e 0%, #3949ab 50%, #1a237e 100%)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2.5,
                        background:
                          "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        boxShadow: "0 4px 12px rgba(26, 35, 126, 0.3)",
                      }}
                    >
                      <Insights sx={{ fontSize: 22, color: "white" }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 800,
                          color: "primary.main",
                          mb: 1,
                          letterSpacing: "1px",
                          textTransform: "uppercase",
                          fontSize: "0.8rem",
                        }}
                      >
                        🤖 AI-Powered Insights
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.primary",
                          mb: 1.5,
                          lineHeight: 1.7,
                          fontSize: "0.9rem",
                        }}
                      >
                        {stats.peakEntry?.time
                          ? `Peak visitor activity detected at ${stats.peakEntry.time} with ${stats.peakEntry.entries} check-ins. Consider increasing staffing during these hours for optimal customer service.`
                          : "Analyzing visitor patterns to provide actionable recommendations..."}
                      </Typography>
                      {stats.totalCars > 0 && stats.totalBikes > 0 && (
                        <Stack
                          direction="row"
                          spacing={1.5}
                          sx={{ mt: 2 }}
                          flexWrap="wrap"
                        >
                          <Chip
                            size="small"
                            label={`${Math.round((stats.totalCars / (stats.totalCars + stats.totalBikes)) * 100)}% Four-Wheeler Traffic`}
                            sx={{
                              bgcolor: alpha("#1a237e", 0.15),
                              color: "primary.main",
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              border: "1px solid",
                              borderColor: alpha("#1a237e", 0.25),
                              px: 1,
                              height: 28,
                            }}
                          />
                          <Chip
                            size="small"
                            label={`⏱️ ${trafficMetrics?.avgDuration ? Math.round(trafficMetrics.avgDuration / 60) : 0} min Avg Stay`}
                            sx={{
                              bgcolor: alpha("#4facfe", 0.15),
                              color: "#0d8bdb",
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              border: "1px solid",
                              borderColor: alpha("#4facfe", 0.3),
                              px: 1,
                              height: 28,
                            }}
                          />
                        </Stack>
                      )}
                    </Box>
                  </Box>
                </motion.div>
              )}

              {/* Section Header: Key Performance Indicators */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 48,
                      height: 48,
                      borderRadius: 2.5,
                      background:
                        "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
                      boxShadow: "0 6px 16px rgba(26, 35, 126, 0.3)",
                    }}
                  >
                    <Speed sx={{ fontSize: 24, color: "white" }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        color: "text.primary",
                        letterSpacing: "-0.5px",
                        fontSize: { xs: "1.2rem", md: "1.4rem" },
                        mb: 0.5,
                      }}
                    >
                      Key Performance Metrics
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.85rem",
                        fontWeight: 500,
                      }}
                    >
                      Real-time occupancy tracking and visitor flow analytics
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Enhanced Summary Cards with Trend Indicators */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    whileHover={{ y: -4 }}
                    style={{ height: "100%" }}
                  >
                    <MetricCard
                      icon={EntryIcon}
                      label="Total Check-ins"
                      value={stats.totalEntries}
                      subtitle="Visitor arrivals tracked"
                      gradient={gradients.green}
                      variant="white"
                      showHeader={true}
                      breakdownItems={[
                        {
                          label: "Peak Time",
                          value: stats.peakEntry?.time || "N/A",
                          prefix: "🕐",
                        },
                        {
                          label: "Peak Volume",
                          value: `${
                            stats.peakEntry?.entries?.toLocaleString() || 0
                          } visitors`,
                          prefix: "📈",
                        },
                      ]}
                      borderColor="rgba(76,175,80,0.3)"
                      isLoading={isFetching || isInitialLoading}
                    />
                  </motion.div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    whileHover={{ y: -4 }}
                    style={{ height: "100%" }}
                  >
                    <MetricCard
                      icon={ExitIcon}
                      label="Total Check-outs"
                      value={stats.totalExits}
                      subtitle="Visitor departures logged"
                      gradient={gradients.redAlt}
                      variant="white"
                      showHeader={true}
                      breakdownItems={[
                        {
                          label: "Peak Time",
                          value: stats.peakExit?.time || "N/A",
                          prefix: "🕐",
                        },
                        {
                          label: "Peak Volume",
                          value: `${
                            stats.peakExit?.exits?.toLocaleString() || 0
                          } visitors`,
                          prefix: "📉",
                        },
                      ]}
                      borderColor="rgba(244,67,54,0.3)"
                      isLoading={isFetching || isInitialLoading}
                    />
                  </motion.div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    whileHover={{ y: -4 }}
                    style={{ height: "100%" }}
                  >
                    <MetricCard
                      icon={DirectionsCar}
                      label="Cars Parked"
                      value={stats.totalCars}
                      subtitle="Four-wheeler occupancy"
                      gradient={gradients.pink}
                      variant="white"
                      showHeader={true}
                      footerText={`${
                        stats.totalCars + stats.totalBikes
                          ? Math.round(
                              (stats.totalCars /
                                (stats.totalCars + stats.totalBikes)) *
                                100,
                            )
                          : 0
                      }% of total parking usage`}
                      borderColor="rgba(240,147,251,0.3)"
                      isLoading={isFetching || isInitialLoading}
                    />
                  </motion.div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    whileHover={{ y: -4 }}
                    style={{ height: "100%" }}
                  >
                    <MetricCard
                      icon={TwoWheeler}
                      label="Bikes Parked"
                      value={stats.totalBikes}
                      subtitle="Two-wheeler occupancy"
                      gradient={gradients.cyan}
                      variant="white"
                      showHeader={true}
                      footerText={`${
                        stats.totalCars + stats.totalBikes
                          ? Math.round(
                              (stats.totalBikes /
                                (stats.totalCars + stats.totalBikes)) *
                                100,
                            )
                          : 0
                      }% of total parking usage`}
                      borderColor="rgba(79,172,254,0.3)"
                      isLoading={isFetching || isInitialLoading}
                    />
                  </motion.div>
                </Grid>
              </Grid>

              {/* Enhanced Peak Performance Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    whileHover={{ scale: 1.02 }}
                    style={{ height: "100%" }}
                  >
                    <StatCard
                      label="Peak Check-in Hour"
                      value={stats.peakEntry?.time || "N/A"}
                      subtitle={`${stats.peakEntry?.entries || 0} arrivals during busiest period`}
                      icon={<TrendingUp />}
                      gradient="green"
                      valueGradient="linear-gradient(135deg, #4caf50 0%, #81c784 100%)"
                      change={
                        stats.peakEntry?.entries
                          ? {
                              value: Math.round(
                                (stats.peakEntry.entries /
                                  Math.max(stats.totalEntries, 1)) *
                                  100,
                              ),
                              label: "of total traffic",
                              isPositive: true,
                            }
                          : undefined
                      }
                      isLoading={isFetching || isInitialLoading}
                    />
                  </motion.div>
                </Grid>

                <Grid item xs={12} md={4}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                    whileHover={{ scale: 1.02 }}
                    style={{ height: "100%" }}
                  >
                    <StatCard
                      label="Peak Check-out Hour"
                      value={stats.peakExit?.time || "N/A"}
                      subtitle={`${stats.peakExit?.exits || 0} departures during busiest period`}
                      icon={<TrendingDown />}
                      gradient="redAlt"
                      valueGradient="linear-gradient(135deg, #f44336 0%, #e57373 100%)"
                      change={
                        stats.peakExit?.exits
                          ? {
                              value: Math.round(
                                (stats.peakExit.exits /
                                  Math.max(stats.totalExits, 1)) *
                                  100,
                              ),
                              label: "of total traffic",
                              isPositive: false,
                            }
                          : undefined
                      }
                      isLoading={isFetching || isInitialLoading}
                    />
                  </motion.div>
                </Grid>

                <Grid item xs={12} md={4}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                    whileHover={{ scale: 1.02 }}
                    style={{ height: "100%" }}
                  >
                    <StatCard
                      label="Average Visit Duration"
                      value={
                        trafficMetrics?.avgDuration
                          ? `${Math.round(trafficMetrics.avgDuration / 60)}m`
                          : "N/A"
                      }
                      subtitle="Typical parking session length"
                      icon={<AccessTime />}
                      gradient="purple"
                      valueGradient="linear-gradient(135deg, #1a237e 0%, #3949ab 100%)"
                      change={
                        trafficMetrics?.avgDuration
                          ? {
                              value: Math.round(
                                trafficMetrics.avgDuration / 60,
                              ),
                              label: "minutes per visit",
                              isPositive: true,
                            }
                          : undefined
                      }
                      isLoading={isFetching || isInitialLoading}
                    />
                  </motion.div>
                </Grid>
              </Grid>

              {/* Enhanced Traffic Trends Chart with Better Context */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.8 }}
                  >
                    <ChartCard
                      title={`${getViewLabel()} Visitor Flow Patterns`}
                      subtitle="Track check-in/check-out trends and real-time occupancy levels across time periods"
                      icon={<ShowChart />}
                      isLoading={isFetching || isInitialLoading}
                      chartHeight={450}
                      chartType={trendsChartType}
                      chartTypeOptions={[
                        { value: "line", label: "Line Chart" },
                        { value: "area", label: "Area Chart" },
                        { value: "bar", label: "Bar Chart" },
                      ]}
                      onChartTypeChange={(value) =>
                        setTrendsChartType(value as ChartType)
                      }
                    >
                      <ResponsiveContainer>
                        {renderTrendsChart()}
                      </ResponsiveContainer>
                    </ChartCard>
                  </motion.div>
                </Grid>
              </Grid>

              {/* Enhanced Vehicle Distribution with Better Labels */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={8}>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.9 }}
                  >
                    <ChartCard
                      title={`Vehicle Type Analysis (${getViewLabel()})`}
                      subtitle="Comparative distribution of cars vs bikes across selected time period"
                      icon={<BarChartIcon />}
                      isLoading={isFetching || isInitialLoading}
                      chartHeight={450}
                      chartType={distributionChartType}
                      chartTypeOptions={[
                        { value: "bar", label: "Stacked Bar" },
                        { value: "area", label: "Area Chart" },
                        { value: "line", label: "Line Chart" },
                      ]}
                      onChartTypeChange={(value) =>
                        setDistributionChartType(value as ChartType)
                      }
                    >
                      <ResponsiveContainer>
                        {renderVehicleDistributionChart()}
                      </ResponsiveContainer>
                    </ChartCard>
                  </motion.div>
                </Grid>

                <Grid item xs={12} md={4}>
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 1.0 }}
                  >
                    <ChartCard
                      title="Parking Composition"
                      subtitle="Overall vehicle type distribution for the selected period"
                      icon={<Assessment />}
                      isLoading={isFetching || isInitialLoading}
                      chartHeight={300}
                      elevation={3}
                    >
                      {renderVehicleTypePieChart()}
                    </ChartCard>
                  </motion.div>
                </Grid>
              </Grid>
            </Box>
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* Scroll to Top Button */}
      <ScrollToTopFab />
    </Box>
  );
};

export default React.memo(Analytics);
